/**
 * perfProbes — medição OPCIONAL por subsistema, pendurada no PerfMeter.
 *
 * Um singleton com buffers FIXOS (nenhum array/objeto/string por quadro): cada
 * subsistema tem um id inteiro (P.*) e o par begin/end acumula tempo e nº de
 * chamadas do quadro corrente em Float64Array/Uint32Array. No fim do quadro
 * (endFrame, chamado pelo PerfMeter.frame) o total do quadro entra num anel de
 * RING quadros por subsistema — é dele que saem média, p95, p99 e pior quadro.
 *
 * DESLIGADO (padrão): begin/end/endFrame retornam na primeira linha — o custo é
 * uma chamada de função monomórfica + um teste de booleano. O ponto mais quente
 * (Fleet.write, ~500 chamadas/quadro) paga ~1.000 dessas por quadro: poucos µs.
 * LIGADO: duas chamadas de performance.now() por par begin/end.
 *
 * As estatísticas (stats/report) SÓ rodam no redesenho do painel (2×/s) e usam
 * um scratch pré-alocado — a ordenação é in-place no subarray. Strings existem
 * apenas aí, nunca no caminho do quadro.
 *
 * ANINHAMENTO: sondas de ids DIFERENTES podem se aninhar (cada id tem seu t0).
 * O tempo do pai INCLUI os filhos — ex.: `labels` inclui as escritas de `atlas`
 * e `linhas` feitas dentro do layout; `hud` inclui os renders de `auxGL`. É
 * proposital: cada linha responde "quanto custa este subsistema por inteiro".
 * A mesma sonda NÃO é reentrante (begin duplo sobrescreve o t0).
 *
 * O relógio é injetável (_now) para o harness ser determinístico:
 * scripts/perfProbesHarness.mjs.
 */

export const P = {
  controles: 0,   // OrbitControls + câmera dinâmica
  entidades: 1,   // EntityManager.update inteiro
  snapshot: 2,    // processamento de snapshot (rede → entidades)
  agentes: 3,     // CityAgents.update (simulação de tráfego/pedestres)
  fleetWrite: 4,  // recomposição de matrizes dos avatares
  fleetFlush: 5,  // faixas de upload dos avatares
  sombras: 6,     // BlobShadows (write + flush)
  atlas: 7,       // LabelAtlas (write + pintura + flush)
  linhas: 8,      // LabelLines (write + hide + flush)
  labels: 9,      // layout + de-clutter dos rótulos (inclui atlas/linhas internos)
  arvores: 10,    // Trees.update
  nuvens: 11,     // updateClouds
  dialogos: 12,   // balões dos edifícios (BuildingDialog)
  hud: 13,        // Hud.update + Achievements.update (inclui auxGL interno)
  composer: 14,   // composer.render (pós-processamento inteiro)
  overlay: 15,    // render da sobreposição dos rótulos
  auxGL: 16       // renderers WebGL auxiliares (banner + chips de conquista)
};

export const PROBE_NAMES = [
  'controles', 'entidades', 'snapshot', 'agentes', 'fleet.write', 'fleet.flush',
  'sombras', 'atlas', 'linhas', 'labels', 'arvores', 'nuvens', 'dialogos',
  'hud', 'composer', 'overlay', 'auxGL'
];

const N = PROBE_NAMES.length;
const RING = 256; // ~4 s de janela a 60 fps; p99 sai de ~2,5 quadros da cauda

class PerfProbes {
  constructor() {
    this.on = false;
    this._now = () => performance.now(); // injetável (harness determinístico)
    this._t0 = new Float64Array(N);         // begin aberto por sonda
    this._frameMs = new Float64Array(N);    // acumulado do quadro corrente
    this._frameCalls = new Uint32Array(N);
    this._ring = new Float32Array(N * RING);      // ms por quadro, por sonda
    this._ringCalls = new Uint32Array(N * RING);  // chamadas por quadro, por sonda
    this._head = 0;
    this._ringN = 0;
    this._scratch = new Float32Array(RING); // p/ ordenar a janela no stats()
    this._out = { avg: 0, p95: 0, p99: 0, worst: 0, calls: 0 }; // reusado por stats()
  }

  enable() {
    this.reset();
    this.on = true;
  }

  disable() {
    this.on = false;
  }

  reset() {
    this._t0.fill(0);
    this._frameMs.fill(0);
    this._frameCalls.fill(0);
    this._head = 0;
    this._ringN = 0;
  }

  /** Abre a medição da sonda `id` (não reentrante para o MESMO id). */
  begin(id) {
    if (!this.on) return;
    this._t0[id] = this._now();
  }

  /** Fecha a medição: soma ao quadro corrente e conta a chamada. */
  end(id) {
    if (!this.on) return;
    const t = this._t0[id];
    if (t === 0) return; // end sem begin (ou sonda aberta antes do enable): ignora
    this._frameMs[id] += this._now() - t;
    this._frameCalls[id]++;
    this._t0[id] = 0;
  }

  /** Fim do quadro renderizado: grava os totais no anel e zera o quadro. */
  endFrame() {
    if (!this.on) return;
    const h = this._head;
    for (let i = 0; i < N; i++) {
      this._ring[i * RING + h] = this._frameMs[i];
      this._ringCalls[i * RING + h] = this._frameCalls[i];
      this._frameMs[i] = 0;
      this._frameCalls[i] = 0;
    }
    this._head = (h + 1) % RING;
    if (this._ringN < RING) this._ringN++;
  }

  /**
   * Estatísticas da janela (anel) da sonda `id`. Reusa `this._out` — copie os
   * campos se precisar guardar. Chamado só no redesenho do painel (2×/s).
   */
  stats(id) {
    const o = this._out;
    const n = this._ringN;
    if (!n) { o.avg = o.p95 = o.p99 = o.worst = o.calls = 0; return o; }
    const base = id * RING;
    const s = this._scratch;
    let soma = 0, chamadas = 0;
    for (let i = 0; i < n; i++) {
      s[i] = this._ring[base + i];
      soma += s[i];
      chamadas += this._ringCalls[base + i];
    }
    const janela = s.subarray(0, n);
    janela.sort(); // TypedArray.sort é numérico e in-place
    o.avg = soma / n;
    o.p95 = janela[Math.min(n - 1, Math.floor(n * 0.95))];
    o.p99 = janela[Math.min(n - 1, Math.floor(n * 0.99))];
    o.worst = janela[n - 1];
    o.calls = chamadas / n; // chamadas por quadro (média da janela)
    return o;
  }

  /** Relatório completo em texto (para o clique-copiar do painel e benchmarks). */
  report() {
    const linhas = ['subsistema      avg     p95     p99   worst  calls/q'];
    for (let i = 0; i < N; i++) {
      const st = this.stats(i);
      if (!st.calls && !st.avg) continue; // sonda sem uso na janela: fora do texto
      linhas.push(
        PROBE_NAMES[i].padEnd(12) +
        st.avg.toFixed(3).padStart(8) +
        st.p95.toFixed(3).padStart(8) +
        st.p99.toFixed(3).padStart(8) +
        st.worst.toFixed(3).padStart(8) +
        st.calls.toFixed(1).padStart(9)
      );
    }
    return linhas.join('\n');
  }

  /** Snapshot numérico (benchmark/JSON). Aloca — não chamar por quadro. */
  snapshot() {
    const out = {};
    for (let i = 0; i < N; i++) {
      const st = this.stats(i);
      if (!st.calls && !st.avg) continue;
      out[PROBE_NAMES[i]] = { avg: st.avg, p95: st.p95, p99: st.p99, worst: st.worst, callsPorQuadro: st.calls };
    }
    return out;
  }
}

export const probes = new PerfProbes();
